// web/src/features/clients/ClientsScreens.tsx
//
// Two screens in one file (same pattern as SuppliersScreens / PosScreens):
//
//   ClientsListScreen   — /clients
//                         KPI strip, search, + New Client, table with stats
//
//   ClientDetailScreen  — /clients/:clientId  (display + edit toggle)
//                         /clients/new        (starts in edit mode, empty form)
//
// Backed by hotfix_028_clients_contacts_and_stats.sql which added:
//   - clients.primary_contact_*  accounts_contact_*  address_*
//     payment_terms_days  website  notes  is_active  xero_contact_id
//   - v_client_stats view
//
// 25 Sep 2026 (mig 389): the client's contracts, MSA and rate cards live in
// ClientContractsCard on the detail page's right rail — table client_documents,
// bucket client-documents, manager+ read/add, director removes.

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase, apiFetch } from '@/lib/supabase';
import { useRequireAuth, hasRole } from '@/lib/auth';
import {
  Card, Pill, Button, Field, Input, Select, Spinner, EmptyState, KPI,
} from '@/components/ui';
import { fmtMoney, fmtDate } from '@/lib/dates';
import {
  Plus, Building2, ExternalLink, Save, X, Edit2,
  MapPin, FileText, Briefcase, DollarSign, ArrowLeft,
  Users, Mail, Phone, Trash2, UserPlus,
  CheckCircle2, AlertTriangle, Search,
} from 'lucide-react';
import { Textarea } from '@/components/ui';
import ClientContractsCard from '@/features/clients/ClientContractsCard';   // mig 389 — 25 Sep 2026

// Xero short code — pulled from env so we can override per environment.
const XERO_SHORTCODE = (import.meta as any).env?.VITE_XERO_SHORTCODE ?? '!QHgP0';

function xeroDeepLink(path: string) {
  return `https://go.xero.com/organisationlogin/default.aspx?shortcode=${XERO_SHORTCODE}&redirecturl=${encodeURIComponent(path)}`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Client {
  client_id: string;
  legal_name: string;
  trading_name: string | null;
  client_code: string | null;
  abn: string | null;
  billing_email: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  primary_contact_phone: string | null;
  accounts_contact_name: string | null;
  accounts_contact_email: string | null;
  accounts_contact_phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  payment_terms_days: number | null;
  website: string | null;
  notes: string | null;
  is_active: boolean;
  xero_contact_id: string | null;
  // T-504 — what the id actually resolved to in Xero, and when. Stored so the
  // link is reviewable: ops and Xero spell the same company differently often
  // enough that an id on its own tells nobody anything.
  xero_contact_name: string | null;
  xero_linked_at: string | null;
  uses_rcti?: boolean;
  created_at?: string;
}

interface ClientStats {
  client_id: string;
  projects_all_time: number;
  projects_12mo: number;
  active_projects: number;
  lifetime_contract_value: number;
  lifetime_invoiced: number;
  lifetime_paid: number;
  outstanding: number;
  last_project_at: string | null;
  open_bids: number;
  won_bids_count: number;
  lost_bids_count: number;
}

interface ProjectRow {
  project_id: string;
  project_code: string;
  project_name: string;
  status: string;
  contract_value: number | null;
  invoiced_to_date: number | null;
  paid_to_date: number | null;
  created_at: string;
  client_id?: string | null;
  source_bid_id?: string | null;
}

interface BidRow {
  bid_id: string;
  bid_number: string;
  bid_title: string;
  status: string;
  estimated_value: number | null;
  due_date: string | null;
  created_at: string;
}

const EMPTY_CLIENT: Client = {
  client_id: '',
  legal_name: '',
  trading_name: null,
  client_code: null,
  abn: null,
  billing_email: null,
  primary_contact_name: null,
  primary_contact_email: null,
  primary_contact_phone: null,
  accounts_contact_name: null,
  accounts_contact_email: null,
  accounts_contact_phone: null,
  address_line1: null,
  address_line2: null,
  suburb: null,
  state: null,
  postcode: null,
  country: 'AU',
  payment_terms_days: 30,
  website: null,
  notes: null,
  is_active: true,
  xero_contact_id: null,
  xero_contact_name: null,
  xero_linked_at: null,
  uses_rcti: false,
};

// ===========================================================================
// LIST SCREEN
// ===========================================================================

export function ClientsListScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<Record<string, ClientStats>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'active' | 'all' | 'inactive'>('active');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      const { data: clientsData, error: cErr } = await supabase
        .from('clients')
        .select('*')
        .order('legal_name');

      if (cErr) {
        if (!cancelled) {
          setError(cErr.message);
          setLoading(false);
        }
        return;
      }

      const ids = (clientsData ?? []).map((c) => c.client_id);
      const statsMap: Record<string, ClientStats> = {};

      if (ids.length > 0) {
        const { data: statsData, error: sErr } = await supabase
          .from('v_client_stats')
          .select('*')
          .in('client_id', ids);

        if (sErr) {
          console.warn('Could not load client stats:', sErr.message);
        } else {
          for (const s of (statsData ?? []) as ClientStats[]) {
            statsMap[s.client_id] = s;
          }
        }
      }

      if (cancelled) return;
      setClients((clientsData ?? []) as Client[]);
      setStats(statsMap);
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (filter === 'active' && !c.is_active) return false;
      if (filter === 'inactive' && c.is_active) return false;
      if (!q) return true;
      return (
        c.legal_name?.toLowerCase().includes(q) ||
        c.trading_name?.toLowerCase().includes(q) ||
        c.client_code?.toLowerCase().includes(q) ||
        c.abn?.toLowerCase().includes(q)
      );
    });
  }, [clients, search, filter]);

  const kpis = useMemo(() => {
    const activeClients = clients.filter((c) => c.is_active).length;
    let activeProjects = 0;
    let outstanding = 0;
    let invoicedLifetime = 0;
    for (const c of clients) {
      const s = stats[c.client_id];
      if (!s) continue;
      activeProjects += s.active_projects;
      outstanding += s.outstanding;
      invoicedLifetime += s.lifetime_invoiced;
    }
    return { activeClients, activeProjects, outstanding, invoicedLifetime };
  }, [clients, stats]);

  // T-505 — Xero readiness. 11 of 12 clients were unlinked the day the AR push
  // went live, and an unlinked client simply cannot be invoiced from Coretel.
  // That is the single most actionable number on this screen, so it gets said
  // out loud rather than hidden one click deep on each client.
  const xero = useMemo(() => {
    const active = clients.filter(c => c.is_active);
    const linked = active.filter(c => !!c.xero_contact_id).length;
    return { linked, total: active.length, missing: active.length - linked };
  }, [clients]);

  // Magnitude, ranked, top few. Deliberately NOT a pie: twelve clients where
  // three carry the money makes a donut unreadable and a bar list obvious.
  const revenueRows = useMemo(() => {
    return clients
      .map(c => ({
        id:    c.client_id,
        name:  c.trading_name || c.legal_name,
        value: stats[c.client_id]?.lifetime_invoiced ?? 0,
      }))
      .filter(r => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [clients, stats]);

  const owedRows = useMemo(() => {
    return clients
      .map(c => ({
        id:    c.client_id,
        name:  c.trading_name || c.legal_name,
        value: stats[c.client_id]?.outstanding ?? 0,
      }))
      .filter(r => r.value > 0.005)
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [clients, stats]);

  const clientsWithWork = useMemo(
    () => clients.filter(c => (stats[c.client_id]?.active_projects ?? 0) > 0).length,
    [clients, stats],
  );

  // T-508 Phase 4 — belt and braces behind the hub guard. This file had NO
  // role check at all, and /clients used to map to the Project hub that field
  // and supervisor both hold, so both could read lifetime invoiced and total
  // outstanding per client. Same shape as BidsScreens.tsx:60.
  // Placed after every hook — an early return above them breaks rules-of-hooks.
  if (user && !hasRole(user, 'manager')) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState
            title="No access"
            description="Clients is restricted to manager-and-above roles. Speak to a director if you think this is a mistake."
            icon={Building2}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-5">
      <Link
        to="/project"
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent"
      >
        <ArrowLeft size={14}/> Project portal
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-text">Clients</h1>
          <p className="text-[13px] text-text-muted mt-0.5">
            The people who pay us. Contact details here flow through to every invoice.
          </p>
        </div>
        <Link to="/clients/new">
          <Button icon={Plus} variant="primary">New client</Button>
        </Link>
      </div>

      {/* Headline numbers. Each one carries the context that makes it mean
          something — "12 clients" alone tells nobody anything. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          label="Active clients"
          value={kpis.activeClients.toString()}
          sub={clientsWithWork > 0 ? `${clientsWithWork} with work on right now` : 'none with active work'}
          icon={Building2}
        />
        <StatTile
          label="Active projects"
          value={kpis.activeProjects.toString()}
          sub="across all clients"
          icon={Briefcase}
        />
        <StatTile
          label="Outstanding"
          value={fmtMoney(kpis.outstanding)}
          sub={owedRows.length === 1 ? 'from 1 client' : `from ${owedRows.length} clients`}
          tone={kpis.outstanding > 0 ? 'warn' : 'ok'}
          icon={DollarSign}
        />
        <StatTile
          label="Lifetime invoiced"
          value={fmtMoney(kpis.invoicedLifetime)}
          sub="every client, all time"
          icon={FileText}
        />
      </div>

      {/* Only shown when there is something to do about it. */}
      {xero.missing > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-warn/30 bg-warn/8 px-4 py-3">
          <AlertTriangle size={15} className="text-warn mt-0.5 flex-shrink-0"/>
          <div className="text-[13px] text-text">
            <span className="font-medium">{xero.linked} of {xero.total} clients are linked to a Xero contact.</span>{' '}
            <span className="text-text-muted">
              The other {xero.missing} can't have an invoice pushed to Xero until they are —
              open a client and use Find in Xero on the Xero card.
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <RankedBars
          title="Revenue by client"
          subtitle="lifetime invoiced"
          rows={revenueRows}
          colour={VIZ_REVENUE}
          empty="Nothing invoiced yet."
        />
        <RankedBars
          title="Owed to us"
          subtitle="outstanding on issued invoices"
          rows={owedRows}
          colour={VIZ_OWED}
          empty="Every client is square. Rare and good."
        />
      </div>

      <Card>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <Input
            placeholder="Search name, code, ABN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="md:flex-1"
          />
          <div className="flex gap-1">
            {(['active', 'all', 'inactive'] as const).map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={
                  'px-3 py-1.5 text-[11px] uppercase tracking-wider2 rounded border transition ' +
                  (filter === k
                    ? 'bg-accent border-accent text-white'
                    : 'bg-ink-raised border-line text-text-muted hover:text-text')
                }
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {error && <Card><div className="text-sm text-danger">⚠ {error}</div></Card>}

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No clients yet"
          body="Add your first client to start building bids, quotes and projects against them."
          action={<Link to="/clients/new"><Button icon={Plus} variant="primary">New client</Button></Link>}
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider2 text-text-muted border-b border-line">
                  <th className="py-3 pl-4 pr-3 font-medium">Client</th>
                  <th className="py-3 px-3 font-medium">Xero</th>
                  <th className="py-3 px-3 font-medium text-right whitespace-nowrap">Projects<span className="text-text-dim"> 12mo</span></th>
                  <th className="py-3 px-3 font-medium text-right">Active</th>
                  <th className="py-3 px-3 font-medium text-right">Invoiced</th>
                  <th className="py-3 px-3 font-medium text-right">Outstanding</th>
                  <th className="py-3 pr-4 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const s = stats[c.client_id];
                  const owing = Number(s?.outstanding ?? 0);
                  return (
                    <tr
                      key={c.client_id}
                      className="border-b border-line/50 last:border-0 hover:bg-ink-hover transition cursor-pointer"
                      onClick={() => navigate(`/clients/${c.client_id}`)}
                    >
                      <td className="py-3.5 pl-4 pr-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-text">{c.legal_name}</span>
                          {!c.is_active && <Pill tone="lost" label="Inactive" small />}
                        </div>
                        <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                          {c.client_code && <span className="font-mono">{c.client_code}</span>}
                          {c.trading_name && c.trading_name !== c.legal_name && <span>t/a {c.trading_name}</span>}
                          {c.abn && <span className="font-mono text-text-dim">ABN {c.abn}</span>}
                        </div>
                      </td>
                      <td className="py-3.5 px-3">
                        {c.xero_contact_id ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-ok" title={c.xero_contact_name ?? 'Linked'}>
                            <CheckCircle2 size={12}/> Linked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-warn" title="Can't push invoices to Xero yet">
                            <AlertTriangle size={12}/> Not linked
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-3 text-right tabular-nums text-text-muted">{s?.projects_12mo ?? 0}</td>
                      <td className="py-3.5 px-3 text-right tabular-nums">
                        {s?.active_projects
                          ? <span className="text-ok font-medium">{s.active_projects}</span>
                          : <span className="text-text-dim">—</span>}
                      </td>
                      <td className="py-3.5 px-3 text-right tabular-nums text-text">{fmtMoney(s?.lifetime_invoiced ?? 0)}</td>
                      <td className="py-3.5 px-3 text-right tabular-nums">
                        {owing > 0.005
                          ? <span className="text-warn font-medium">{fmtMoney(owing)}</span>
                          : <span className="text-text-dim">—</span>}
                      </td>
                      <td className="py-3.5 pr-4 text-right text-text-dim">›</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ===========================================================================
// DETAIL SCREEN (handles /clients/:clientId and /clients/new)
// ===========================================================================

export function ClientDetailScreen() {
  const user = useRequireAuth();
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const isNew = !clientId || clientId === 'new';

  const [client, setClient] = useState<Client>(EMPTY_CLIENT);
  const [draft, setDraft] = useState<Client>(EMPTY_CLIENT);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [bids, setBids] = useState<BidRow[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(isNew);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      setEditMode(true);
      setClient(EMPTY_CLIENT);
      setDraft(EMPTY_CLIENT);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      const [{ data: c, error: cErr }, { data: s }] = await Promise.all([
        supabase.from('clients').select('*').eq('client_id', clientId!).single(),
        supabase.from('v_client_stats').select('*').eq('client_id', clientId!).maybeSingle(),
      ]);

      if (cErr) {
        if (!cancelled) {
          setError(cErr.message);
          setLoading(false);
        }
        return;
      }

      if (cancelled) return;
      setClient(c as Client);
      setDraft(c as Client);
      setStats((s ?? null) as ClientStats | null);

      // Projects — try direct, fall back via bid
      const { data: projData } = await supabase
        .from('projects')
        .select('project_id, project_code, project_name, status, contract_value, invoiced_to_date, paid_to_date, created_at, client_id, source_bid_id')
        .order('created_at', { ascending: false });

      let clientProjects: ProjectRow[] = [];
      if (projData && projData.length > 0) {
        const directMatches = (projData as ProjectRow[]).filter((p) => p.client_id === clientId);
        if (directMatches.length > 0) {
          clientProjects = directMatches.slice(0, 25);
        } else {
          const bidIds = (projData as ProjectRow[]).map((p) => p.source_bid_id).filter(Boolean) as string[];
          if (bidIds.length > 0) {
            const { data: bidClients } = await supabase
              .from('bids')
              .select('bid_id, client_id')
              .in('bid_id', bidIds)
              .eq('client_id', clientId!);
            const matchedBidIds = new Set((bidClients ?? []).map((b: any) => b.bid_id));
            clientProjects = (projData as ProjectRow[])
              .filter((p) => p.source_bid_id && matchedBidIds.has(p.source_bid_id))
              .slice(0, 25);
          }
        }
      }

      const { data: bidsData } = await supabase
        .from('bids')
        .select('bid_id, bid_number, bid_title, status, estimated_value, due_date, created_at, client_id')
        .eq('client_id', clientId!)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!cancelled) {
        setProjects(clientProjects);
        setBids((bidsData ?? []) as BidRow[]);
        setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [clientId, isNew, reloadKey]);

  function buildPayload(): Record<string, any> {
    return {
      legal_name:             draft.legal_name.trim(),
      trading_name:           draft.trading_name?.trim() || null,
      client_code:            draft.client_code?.trim() || null,
      abn:                    draft.abn?.trim() || null,
      billing_email:          draft.billing_email?.trim() || null,
      primary_contact_name:   draft.primary_contact_name?.trim() || null,
      primary_contact_email:  draft.primary_contact_email?.trim() || null,
      primary_contact_phone:  draft.primary_contact_phone?.trim() || null,
      accounts_contact_name:  draft.accounts_contact_name?.trim() || null,
      accounts_contact_email: draft.accounts_contact_email?.trim() || null,
      accounts_contact_phone: draft.accounts_contact_phone?.trim() || null,
      address_line1:          draft.address_line1?.trim() || null,
      address_line2:          draft.address_line2?.trim() || null,
      suburb:                 draft.suburb?.trim() || null,
      state:                  draft.state?.trim() || null,
      postcode:               draft.postcode?.trim() || null,
      country:                draft.country ?? 'AU',
      payment_terms_days:     draft.payment_terms_days ?? 30,
      website:                draft.website?.trim() || null,
      notes:                  draft.notes?.trim() || null,
      is_active:              draft.is_active ?? true,
      xero_contact_id:        draft.xero_contact_id?.trim() || null,
    };
  }

  async function handleSave() {
    if (!draft.legal_name?.trim()) {
      setError('Legal name is required.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isNew) {
        const { data, error: iErr } = await supabase
          .from('clients')
          .insert(buildPayload())
          .select()
          .single();

        if (iErr) throw iErr;
        navigate(`/clients/${data.client_id}`, { replace: true });
      } else {
        const { error: uErr } = await supabase
          .from('clients')
          .update(buildPayload())
          .eq('client_id', clientId!);

        if (uErr) throw uErr;
        setClient(draft);
        setEditMode(false);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function handleCancelEdit() {
    if (isNew) {
      navigate('/clients');
    } else {
      setDraft(client);
      setEditMode(false);
      setError(null);
    }
  }

  function update<K extends keyof Client>(key: K, value: Client[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  // T-508 Phase 4 — belt and braces behind the hub guard. This file had NO
  // role check at all, and /clients used to map to the Project hub that field
  // and supervisor both hold, so both could read lifetime invoiced and total
  // outstanding per client. Same shape as BidsScreens.tsx:60.
  // Placed after every hook — an early return above them breaks rules-of-hooks.
  if (user && !hasRole(user, 'manager')) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState
            title="No access"
            description="Clients is restricted to manager-and-above roles. Speak to a director if you think this is a mistake."
            icon={Building2}
          />
        </Card>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner /></div>;
  }

  const headerName = editMode ? (draft.legal_name || 'New client') : client.legal_name;

  // T-505 — in view mode a card with nothing in it is noise. Hardline showed
  // three contact blocks, two of them entirely empty dashes.
  const hasPrimaryContact = !!(client.primary_contact_name || client.primary_contact_email || client.primary_contact_phone);

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-5">
      <Link to="/clients" className="text-[12px] text-text-muted hover:text-accent inline-flex items-center gap-1.5">
        <ArrowLeft size={14}/> Clients
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-text">{headerName || '—'}</h1>
          {!editMode && client.trading_name && client.trading_name !== client.legal_name && (
            <div className="text-sm text-text-muted">trading as {client.trading_name}</div>
          )}
          {!editMode && (
            <div className="flex items-center gap-2 mt-1 text-xs text-text-muted">
              {client.client_code && <span className="font-mono">{client.client_code}</span>}
              {client.abn && <span>· ABN {client.abn}</span>}
              {!client.is_active && <Pill tone="lost" label="Inactive" />}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button icon={X} variant="secondary" onClick={handleCancelEdit} disabled={saving}>Cancel</Button>
              <Button icon={Save} variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : isNew ? 'Create client' : 'Save'}
              </Button>
            </>
          ) : (
            <Button icon={Edit2} variant="secondary" onClick={() => setEditMode(true)}>Edit</Button>
          )}
        </div>
      </div>

      {error && <Card><div className="text-sm text-danger">⚠ {error}</div></Card>}

      {!editMode && !isNew && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile
            label="Projects" value={stats.projects_12mo.toString()}
            sub={stats.active_projects > 0 ? `${stats.active_projects} running now` : 'none running'}
            icon={Briefcase}
          />
          <StatTile
            label="Lifetime invoiced" value={fmtMoney(stats.lifetime_invoiced)}
            sub={`${fmtMoney(stats.lifetime_paid)} paid`}
            icon={FileText}
          />
          <StatTile
            label="Outstanding" value={fmtMoney(stats.outstanding)}
            sub={stats.outstanding > 0 ? 'owed to us' : 'all square'}
            tone={stats.outstanding > 0 ? 'warn' : 'ok'}
            icon={DollarSign}
          />
          <StatTile
            label="Bids won"
            value={`${stats.won_bids_count} / ${stats.won_bids_count + stats.lost_bids_count + stats.open_bids}`}
            sub={stats.open_bids > 0 ? `${stats.open_bids} still open` : 'none open'}
            icon={Building2}
          />
        </div>
      )}

      {/* T-505b — the card grid below is the EDIT FORM. View mode has its own
          layout in <ClientOverview/>, grouped by question rather than by
          database table. (The unreachable view branches still inside these
          cards are legacy and due a tidy-up.) */}
      {editMode ? (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card>
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-text-muted">
              <Building2 size={14} /> Basics
            </div>
            {editMode ? (
              <div className="space-y-3">
                <Field label="Legal name *" required>
                  <Input value={draft.legal_name ?? ''} onChange={(e) => update('legal_name', e.target.value)} autoFocus />
                </Field>
                <Field label="Trading name">
                  <Input value={draft.trading_name ?? ''} onChange={(e) => update('trading_name', e.target.value || null)} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Client code">
                    <Input value={draft.client_code ?? ''} onChange={(e) => update('client_code', e.target.value || null)} placeholder="e.g. CL-001" />
                  </Field>
                  <Field label="ABN">
                    <Input value={draft.abn ?? ''} onChange={(e) => update('abn', e.target.value || null)} placeholder="11 digits" />
                  </Field>
                </div>
                <Field label="Website">
                  <Input value={draft.website ?? ''} onChange={(e) => update('website', e.target.value || null)} placeholder="https://…" />
                </Field>
                <Field label="Active">
                  <Select value={draft.is_active ? 'true' : 'false'} onChange={(e) => update('is_active', e.target.value === 'true')}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Select>
                </Field>
              </div>
            ) : (
              <dl className="space-y-2 text-sm">
                <InfoRow label="Legal name" value={client.legal_name} />
                <InfoRow label="Trading name" value={client.trading_name} />
                <InfoRow label="Client code" value={client.client_code} mono />
                <InfoRow label="ABN" value={client.abn} mono />
                <InfoRow label="Website" value={client.website ? (
                  <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent-bright underline">
                    {client.website}
                  </a>
                ) : null} />
              </dl>
            )}
          </Card>
  
          {(editMode || hasPrimaryContact) && (
          <Card>
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-text-muted">
              <Briefcase size={14} /> Primary contact
            </div>
            {editMode ? (
              <div className="space-y-3">
                <Field label="Name">
                  <Input value={draft.primary_contact_name ?? ''} onChange={(e) => update('primary_contact_name', e.target.value || null)} />
                </Field>
                <Field label="Email">
                  <Input type="email" value={draft.primary_contact_email ?? ''} onChange={(e) => update('primary_contact_email', e.target.value || null)} />
                </Field>
                <Field label="Phone">
                  <Input value={draft.primary_contact_phone ?? ''} onChange={(e) => update('primary_contact_phone', e.target.value || null)} />
                </Field>
              </div>
            ) : (
              <dl className="space-y-2 text-sm">
                <InfoRow label="Name" value={client.primary_contact_name} />
                <InfoRow label="Email" value={client.primary_contact_email ? (
                  <a href={`mailto:${client.primary_contact_email}`} className="text-accent hover:text-accent-bright underline">
                    {client.primary_contact_email}
                  </a>
                ) : null} />
                <InfoRow label="Phone" value={client.primary_contact_phone} />
              </dl>
            )}
          </Card>
          )}
  
          <Card className="lg:col-span-2 border-accent/30">
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-accent">
              <DollarSign size={14} /> Accounts contact (invoice recipient)
            </div>
            <div className="text-xs text-text-muted mb-3">
              Used as the default CONTACT / recipient when creating invoices for projects against this client.
            </div>
            {editMode ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Name">
                  <Input value={draft.accounts_contact_name ?? ''} onChange={(e) => update('accounts_contact_name', e.target.value || null)} />
                </Field>
                <Field label="Email">
                  <Input type="email" value={draft.accounts_contact_email ?? ''} onChange={(e) => update('accounts_contact_email', e.target.value || null)} placeholder="accounts@…" />
                </Field>
                <Field label="Phone">
                  <Input value={draft.accounts_contact_phone ?? ''} onChange={(e) => update('accounts_contact_phone', e.target.value || null)} />
                </Field>
                <Field label="Billing email (fallback)">
                  <Input type="email" value={draft.billing_email ?? ''} onChange={(e) => update('billing_email', e.target.value || null)} />
                </Field>
                <Field label="Payment terms (days)">
                  <Input
                    type="number"
                    value={draft.payment_terms_days?.toString() ?? '30'}
                    onChange={(e) => update('payment_terms_days', parseInt(e.target.value, 10) || 30)}
                  />
                </Field>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <InfoRow label="Name" value={client.accounts_contact_name} />
                <InfoRow label="Email" value={client.accounts_contact_email ? (
                  <a href={`mailto:${client.accounts_contact_email}`} className="text-accent hover:text-accent-bright underline">
                    {client.accounts_contact_email}
                  </a>
                ) : null} />
                <InfoRow label="Phone" value={client.accounts_contact_phone} />
                <InfoRow label="Billing email" value={client.billing_email} />
                <InfoRow label="Payment terms" value={client.payment_terms_days ? `${client.payment_terms_days} days` : null} />
              </div>
            )}
          </Card>
  
          <Card>
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-text-muted">
              <MapPin size={14} /> Billing address
            </div>
            {editMode ? (
              <div className="space-y-3">
                <Field label="Address line 1">
                  <Input value={draft.address_line1 ?? ''} onChange={(e) => update('address_line1', e.target.value || null)} />
                </Field>
                <Field label="Address line 2">
                  <Input value={draft.address_line2 ?? ''} onChange={(e) => update('address_line2', e.target.value || null)} />
                </Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Suburb">
                    <Input value={draft.suburb ?? ''} onChange={(e) => update('suburb', e.target.value || null)} />
                  </Field>
                  <Field label="State">
                    <Input value={draft.state ?? ''} onChange={(e) => update('state', e.target.value || null)} placeholder="QLD" />
                  </Field>
                  <Field label="Postcode">
                    <Input value={draft.postcode ?? ''} onChange={(e) => update('postcode', e.target.value || null)} />
                  </Field>
                </div>
                <Field label="Country">
                  <Input value={draft.country ?? 'AU'} onChange={(e) => update('country', e.target.value || 'AU')} />
                </Field>
              </div>
            ) : (
              <div className="text-sm text-text-muted">
                {[
                  client.address_line1,
                  client.address_line2,
                  [client.suburb, client.state, client.postcode].filter(Boolean).join(' '),
                  client.country && client.country !== 'AU' ? client.country : null,
                ].filter(Boolean).map((line, i) => <div key={i}>{line}</div>)}
                {!client.address_line1 && (
                  <div className="text-text-dim italic">
                    No address on file — Xero statements print this address, and without it they read “No address”.
                  </div>
                )}
              </div>
            )}
          </Card>
  
          <Card>
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-text-muted">
              <ExternalLink size={14} /> Xero connection
            </div>
            {editMode ? (
              <div className="space-y-3">
                <Field label="Xero contact ID">
                  <Input
                    value={draft.xero_contact_id ?? ''}
                    onChange={(e) => update('xero_contact_id', e.target.value || null)}
                    placeholder="GUID from Xero contact URL"
                  />
                  <div className="text-xs text-text-dim mt-1">
                    Manual override. The normal way is Find / Create in Xero on the view screen — that records the contact NAME too, which is what makes the link reviewable.
                  </div>
                </Field>
              </div>
            ) : (
              <div className="text-sm">
                <XeroContactLink
                  clientId={client.client_id}
                  clientName={client.legal_name}
                  contactId={client.xero_contact_id}
                  contactName={client.xero_contact_name}
                  onChanged={() => setReloadKey(k => k + 1)}
                />
              </div>
            )}
          </Card>
  
          {/* T-505 — notes used to live inside the Xero card, which put the one
              thing that decides whether this client can be invoiced under a
              heading about notes. Its own card, and it stays out of the way
              entirely when there is nothing written. */}
          {(editMode || client.notes) && (
            <Card>
              <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-text-muted">
                <FileText size={14} /> Internal notes
              </div>
              {editMode ? (
                <textarea
                  value={draft.notes ?? ''}
                  onChange={(e) => update('notes', e.target.value || null)}
                  className="w-full rounded border border-line bg-ink-raised px-3 py-2 text-sm text-text min-h-[80px]"
                  placeholder="Not shown to the client and never sent to Xero"
                />
              ) : (
                <div className="text-sm text-text whitespace-pre-wrap">{client.notes}</div>
              )}
            </Card>
          )}
        </div>
      ) : !isNew ? (
        <ClientOverview
          client={client}
          clientId={clientId!}
          projects={projects}
          bids={bids}
          onXeroChanged={() => setReloadKey(k => k + 1)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function InfoRow({ label, value, mono = false }: { label: string; value: any; mono?: boolean }) {
  if (value === null || value === undefined || value === '') {
    return (
      <div className="flex gap-4">
        <dt className="w-32 text-text-muted flex-shrink-0">{label}</dt>
        <dd className="text-text-dim italic">—</dd>
      </div>
    );
  }
  return (
    <div className="flex gap-4">
      <dt className="w-32 text-text-muted flex-shrink-0">{label}</dt>
      <dd className={'text-text ' + (mono ? 'font-mono text-xs' : '')}>{value}</dd>
    </div>
  );
}

function statusTone(status: string): any {
  const s = (status ?? '').toLowerCase();
  if (s.includes('complete') || s === 'closed') return 'ok';
  if (s.includes('mobilis') || s.includes('progress') || s.includes('scheduled')) return 'won';
  if (s === 'cancelled' || s === 'lost') return 'lost';
  return 'default';
}

function bidStatusTone(status: string): any {
  const s = (status ?? '').toLowerCase();
  if (s === 'won') return 'won';
  if (s === 'lost') return 'lost';
  if (s === 'open' || s === 'bidding' || s === 'submitted') return 'bid';
  return 'default';
}

// =============================================================================
// T-206 — ClientContactsSection
// CRUD UI for `client_contacts` (table created in migration 102 / T-160).
// Used by HOP submission recipient picker + any future client-facing comms.
// =============================================================================

interface ClientContact {
  contact_id: string;
  client_id: string;
  full_name: string;
  email: string;
  role_title: string | null;
  phone: string | null;
  notes: string | null;
  is_active: boolean;
  state: string | null;            // mig 393 — which state this contact covers (null = all)
  is_field_manager: boolean;       // mig 393/394 — the one client name the crew may call from site
}

const EMPTY_CONTACT: Omit<ClientContact, 'contact_id' | 'client_id' | 'is_active'> = {
  full_name: '', email: '', role_title: null, phone: null, notes: null, state: null, is_field_manager: false,
};

function ClientContactsSection({ clientId }: { clientId: string }) {
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ClientContact | 'new' | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    // (supabase as any) — state / is_field_manager (mig 393) not in database.types.ts yet
    const { data, error: e } = await (supabase as any)
      .from('client_contacts')
      .select('contact_id, client_id, full_name, email, role_title, phone, notes, is_active, state, is_field_manager')
      .eq('client_id', clientId)
      .eq('is_active', true)
      .order('full_name');
    if (e) setError(e.message);
    else setContacts((data ?? []) as ClientContact[]);
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  async function handleSoftDelete(contact: ClientContact) {
    if (!confirm(`Remove ${contact.full_name} from this client's contacts?\n\nThey'll be hidden from the contacts list and the HOP submission recipient picker. Existing HOP submissions historically sent to them are unaffected (audit trail preserved).`)) return;
    const { error: e } = await supabase
      .from('client_contacts')
      .update({ is_active: false })
      .eq('contact_id', contact.contact_id);
    if (e) {
      alert(`Couldn't remove contact: ${e.message}`);
      return;
    }
    load();
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-text-muted">
          <Users size={14}/> Client contacts {contacts.length > 0 && <span>· {contacts.length}</span>}
        </div>
        <Button icon={UserPlus} variant="secondary" size="sm" onClick={() => setEditing('new')}>
          Add contact
        </Button>
      </div>

      {error && (
        <div className="text-sm text-danger mb-3">⚠ {error}</div>
      )}

      {loading ? (
        <div className="py-6 flex justify-center"><Spinner /></div>
      ) : contacts.length === 0 ? (
        <div className="text-sm text-text-dim italic py-4">
          No contacts on file yet. Add one to send HOP submissions to this client.
        </div>
      ) : (
        <div className="space-y-2">
          {contacts.map(c => (
            <div key={c.contact_id} className="border border-line rounded p-3 flex items-start justify-between gap-3 hover:bg-ink-raised/40">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-text truncate flex items-center gap-2 flex-wrap">
                  {c.full_name}
                  {/* mig 394 — only field managers are visible to the crew; everyone else is office-only */}
                  {c.is_field_manager && <Pill label={`Field manager${c.state ? ` · ${c.state}` : ""} — crew can call`} tone="ok" small/>}
                </div>
                {c.role_title && (
                  <div className="text-xs text-text-muted">{c.role_title}</div>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[12px]">
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-accent hover:underline">
                    <Mail size={11}/> {c.email}
                  </a>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-text-muted hover:text-text">
                      <Phone size={11}/> {c.phone}
                    </a>
                  )}
                </div>
                {c.notes && (
                  <div className="text-[11px] text-text-muted mt-1 italic">{c.notes}</div>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  onClick={() => setEditing(c)}
                  className="text-text-muted hover:text-accent p-1.5 rounded hover:bg-ink-raised"
                  title="Edit contact"
                  type="button"
                ><Edit2 size={13}/></button>
                <button
                  onClick={() => handleSoftDelete(c)}
                  className="text-text-muted hover:text-lost p-1.5 rounded hover:bg-ink-raised"
                  title="Remove contact"
                  type="button"
                ><Trash2 size={13}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ClientContactEditor
          clientId={clientId}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </Card>
  );
}

function ClientContactEditor({
  clientId, existing, onClose, onSaved,
}: {
  clientId: string;
  existing: ClientContact | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState({
    full_name:  existing?.full_name  ?? '',
    email:      existing?.email      ?? '',
    role_title: existing?.role_title ?? '',
    phone:      existing?.phone      ?? '',
    notes:      existing?.notes      ?? '',
    state:      existing?.state      ?? '',
    is_field_manager: existing?.is_field_manager ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null);
    if (!draft.full_name.trim()) { setErr('Full name is required.'); return; }
    if (!draft.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) {
      setErr('Valid email is required.');
      return;
    }
    setSaving(true);
    const payload = {
      client_id:  clientId,
      full_name:  draft.full_name.trim(),
      email:      draft.email.trim().toLowerCase(),
      role_title: draft.role_title.trim() || null,
      phone:      draft.phone.trim() || null,
      notes:      draft.notes.trim() || null,
      is_active:  true,
      state:      draft.state || null,
      is_field_manager: draft.is_field_manager,
    };
    if (draft.is_field_manager && !draft.phone.trim()) { setSaving(false); setErr('A field manager needs a phone number — that is what the crew calls.'); return; }
    const { error: e } = existing
      ? await (supabase as any).from('client_contacts').update(payload).eq('contact_id', existing.contact_id)
      : await (supabase as any).from('client_contacts').insert(payload);
    setSaving(false);
    if (e) {
      // Most likely failure: unique(client_id, email) collision when adding
      // an email already present (even soft-deleted).
      setErr(e.message.includes('duplicate') || e.message.includes('unique')
        ? 'A contact with this email already exists for this client (it may be soft-deleted — check "Show inactive" filter, or use a different email).'
        : e.message);
      return;
    }
    onSaved();
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-md w-full max-w-[500px] my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="font-medium">{existing ? 'Edit contact' : 'Add contact'}</div>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-3">
          {err && (
            <div className="text-[12px] text-danger border border-danger/30 bg-danger/10 rounded px-3 py-2">{err}</div>
          )}

          <Field label="Full name *">
            <Input
              value={draft.full_name}
              onChange={(e) => setDraft(d => ({ ...d, full_name: e.target.value }))}
              placeholder="e.g. Sarah Mitchell"
              autoFocus
            />
          </Field>
          <Field label="Email *">
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft(d => ({ ...d, email: e.target.value }))}
              placeholder="sarah.mitchell@hardline.com.au"
            />
          </Field>
          <Field label="Role / title" hint="e.g. Acceptance Manager, Site Engineer">
            <Input
              value={draft.role_title}
              onChange={(e) => setDraft(d => ({ ...d, role_title: e.target.value }))}
            />
          </Field>
          <Field label="Phone">
            <Input
              value={draft.phone}
              onChange={(e) => setDraft(d => ({ ...d, phone: e.target.value }))}
              placeholder="+61 4XX XXX XXX"
            />
          </Field>
          {/* mig 393/394 — the Site folder shows the crew the client's FIELD
              MANAGER for the site's state, and nobody else on this list.
              James, 27 Sep: "our field workers have no right to be able to
              contact some of those guys." */}
          <div className="rounded border border-line p-3 space-y-2">
            <label className="flex items-start gap-2 text-[13px] text-text cursor-pointer">
              <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#FF6A1A]" checked={draft.is_field_manager}
                     onChange={(e) => setDraft(d => ({ ...d, is_field_manager: e.target.checked }))}/>
              <span><b>Field manager</b> — the crew can see and call this person from the Site folder. Leave unticked for PMs, finance, PEs: office-only.</span>
            </label>
            <Field label="State this contact covers" hint="Leave blank for all states">
              <Select value={draft.state} onChange={(e) => setDraft(d => ({ ...d, state: e.target.value }))}>
                <option value="">All states</option>
                {["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"].map(st => <option key={st} value={st}>{st}</option>)}
              </Select>
            </Field>
          </div>
          <Field label="Notes" hint="Optional — preferences, when to copy, etc.">
            <Textarea
              rows={2}
              value={draft.notes}
              onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))}
            />
          </Field>
        </div>

        <div className="px-4 py-3 border-t border-line flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button icon={Save} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : existing ? 'Save changes' : 'Add contact'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// XeroContactLink — T-504
//
// A client invoice cannot go to Xero until the client points at a real Xero
// contact. That link is deliberately a human decision rather than a name
// match: "Ventia Telecommunications" here is "VISIONSTREAM AUSTRALIA PTY LTD"
// in Xero, and getting it wrong creates a second customer with no history,
// no terms and no email — which then drops off the aged receivables report.
//
// So this shows three things plainly: whether it is linked, WHAT it is linked
// to, and the two ways to fix it if it is not.
// ---------------------------------------------------------------------------
function XeroContactLink({
  clientId, clientName, contactId, contactName, onChanged,
}: {
  clientId: string;
  clientName: string;
  contactId: string | null;
  contactName: string | null;
  onChanged: () => void;
}) {
  const [picking, setPicking]   = useState(false);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<{ id: string; name: string } | null>(null);

  async function link(xeroContactId: string) {
    setBusy(true); setError(null);
    try {
      const res = await apiFetch('/xero/contacts/link', {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId, xero_contact_id: xeroContactId }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.detail ?? j?.error ?? `Link failed (${res.status})`);
      setPicking(false); setSuggestion(null);
      onChanged();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function createInXero() {
    setBusy(true); setError(null); setSuggestion(null);
    try {
      const res = await apiFetch('/xero/contacts/create', {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.status === 409 && j?.error === 'contact_exists') {
        setSuggestion({ id: j.xero_contact_id, name: j.xero_contact_name });
        setError(j.detail ?? 'Xero already has a contact with this name.');
        return;
      }
      if (!res.ok) throw new Error((j?.messages?.join(' ') || j?.detail || j?.error) ?? `Create failed (${res.status})`);
      onChanged();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      {contactId ? (
        <div className="flex items-start gap-2">
          <CheckCircle2 size={15} className="text-ok mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-text text-sm">
              Linked to <span className="font-medium">{contactName ?? 'a Xero contact'}</span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <a href={xeroDeepLink(`/Contacts/View.aspx?contactID=${contactId}`)}
                 target="_blank" rel="noopener noreferrer"
                 className="text-[12px] text-accent hover:text-accent-bright underline">Open in Xero →</a>
              <button onClick={() => setPicking(true)}
                      className="text-[12px] text-text-muted hover:text-text underline">Change</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <AlertTriangle size={15} className="text-warn mt-0.5 flex-shrink-0" />
          <div className="min-w-0 w-full">
            <div className="text-text text-sm">Not linked to Xero</div>
            <div className="text-[12px] text-text-dim mt-0.5">
              Invoices for this client can't be pushed to Xero until it is.
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button variant="secondary" size="sm" icon={Search}
                      onClick={() => setPicking(true)} disabled={busy}>
                Find in Xero
              </Button>
              <Button variant="secondary" size="sm" icon={UserPlus}
                      onClick={createInXero} disabled={busy}>
                {busy ? 'Working…' : 'Create in Xero'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="text-[12px] text-danger bg-danger/10 border border-danger/30 rounded px-2.5 py-2">
          {error}
          {suggestion && (
            <button onClick={() => link(suggestion.id)} disabled={busy}
                    className="block mt-1.5 text-accent hover:text-accent-bright underline">
              Link to “{suggestion.name}” instead
            </button>
          )}
        </div>
      )}

      {picking && (
        <XeroContactPicker
          clientName={clientName}
          currentContactId={contactId}
          busy={busy}
          onPick={link}
          onClose={() => { setPicking(false); setError(null); }}
        />
      )}
    </div>
  );
}

// The picker reads Xero's live contact list rather than a stored copy — a
// contact added in Xero five minutes ago has to be selectable, and there is no
// version of this where a stale mirror is the safer option.
function XeroContactPicker({
  clientName, currentContactId, busy, onPick, onClose,
}: {
  clientName: string;
  // The contact THIS client already points at. Without it the "already linked"
  // guard greys out the client's own contact, so Change opens a picker where
  // the only right answer is unclickable.
  currentContactId: string | null;
  busy: boolean;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState(clientName.split(/\s+/)[0] ?? '');
  const [rows, setRows] = useState<any[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRows(null); setLoadError(null);
      try {
        const res = await apiFetch(`/xero/contacts?q=${encodeURIComponent(q)}`, { method: 'GET' });
        const j = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) { setLoadError(j?.detail ?? j?.error ?? `Couldn't read Xero contacts (${res.status})`); return; }
        setRows(j.contacts ?? []);
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message ?? String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [q]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-[560px] mt-16"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="text-sm font-semibold text-text">Link {clientName} to a Xero contact</div>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Xero contacts…" />

          {loadError && <div className="text-[12px] text-danger">{loadError}</div>}

          {rows === null && !loadError && (
            <div className="py-8 text-center text-[12px] text-text-muted italic">Reading Xero…</div>
          )}

          {rows && rows.length === 0 && (
            <div className="py-8 text-center text-[12px] text-text-muted italic">
              No Xero contact matches “{q}”.
            </div>
          )}

          {rows && rows.length > 0 && (
            <div className="max-h-[380px] overflow-y-auto divide-y divide-line border border-line rounded">
              {rows.map((r: any) => (
                <button
                  key={r.xero_contact_id}
                  disabled={busy || (!!r.linked_to && r.xero_contact_id !== currentContactId)}
                  onClick={() => onPick(r.xero_contact_id)}
                  className="w-full text-left px-3 py-2.5 hover:bg-ink-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-text">{r.name}</span>
                    {r.is_customer && <Pill label="customer" tone="ok" small />}
                    {r.is_supplier && !r.is_customer && <Pill label="supplier" tone="default" small />}
                  </div>
                  <div className="text-[11px] text-text-dim mt-0.5">
                    {r.xero_contact_id === currentContactId
                      ? 'Currently linked — pick it again to refresh the stored name'
                      : r.linked_to
                        ? `Already linked to ${r.linked_to}`
                        : (r.email ?? 'No email on the Xero contact')}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Widgets — T-505
//
// Chart colours are the Coretel orange and the app's info blue, stepped so
// that ONE value passes in both light and dark mode: lightness band, chroma
// floor, colourblind separation (ΔE 24 protan / 33 tritan) and 3:1 contrast
// against both surfaces. Validated, not eyeballed.
//
// Every bar is direct-labelled with its value, so the colour carries emphasis
// and never meaning on its own.
// ---------------------------------------------------------------------------
const VIZ_REVENUE = '#E85F14';   // Coretel orange, stepped for chart use
const VIZ_OWED    = '#3A7DC7';   // info blue

function StatTile({ label, value, sub, tone, icon: Icon }: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'ok' | 'warn';
  icon?: any;
}) {
  const valueClass =
    tone === 'warn' ? 'text-warn' :
    tone === 'ok'   ? 'text-text' :
                      'text-text';
  return (
    <div className="rounded-lg border border-line bg-ink-card px-4 py-3.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider2 text-text-muted">
        {Icon && <Icon size={11}/>}
        {label}
      </div>
      <div className={`text-[22px] font-semibold tracking-tight mt-1.5 ${valueClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-text-dim mt-0.5">{sub}</div>}
    </div>
  );
}

// A ranked horizontal bar list. Bars are the right form here because the job
// is "compare magnitude, low to high" — and with twelve clients where three
// carry the money, a pie would be three slivers and a wedge.
function RankedBars({ title, subtitle, rows, colour, empty }: {
  title: string;
  subtitle: string;
  rows: Array<{ id: string; name: string; value: number }>;
  colour: string;
  empty: string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0);
  return (
    <Card>
      <div className="p-4">
        <div className="text-[13px] font-semibold text-text">{title}</div>
        <div className="text-[11px] text-text-muted mb-3">{subtitle}</div>

        {rows.length === 0 ? (
          <div className="py-8 text-center text-[12px] text-text-muted italic">{empty}</div>
        ) : (
          <div className="space-y-2.5">
            {rows.map(r => (
              <Link key={r.id} to={`/clients/${r.id}`} className="block group">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <span className="text-[12px] text-text truncate group-hover:text-accent transition">{r.name}</span>
                  <span className="text-[12px] tabular-nums text-text-muted flex-shrink-0">{fmtMoney(r.value)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-line/60 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: max > 0 ? `${Math.max(2, (r.value / max) * 100)}%` : '0%',
                      backgroundColor: colour,
                    }}
                  />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ClientOverview — T-505b
//
// The detail screen used to be a stack of cards that mirrored the database
// table: Basics, Primary contact, Accounts contact, Billing address, Xero.
// That is how the data is stored, not how anybody reads it — you ended up with
// a half-empty Basics box, a full-width accent band, and a column of dashes
// for every field nobody had filled in.
//
// Grouped by QUESTION instead:
//   right rail  — who are they, and can we actually bill them?
//                 (accounts email, terms, address, Xero link, ABN)
//   main column — what work do we do for them?
//                 (projects, bids, the people we deal with)
//
// Empty facts are dropped rather than dashed. The ones that are empty AND
// matter — no accounts email, no ABN, no address, no Xero link — are called
// out, because each of those breaks something downstream.
// ---------------------------------------------------------------------------
function ClientOverview({
  client, clientId, projects, bids, onXeroChanged,
}: {
  client: Client;
  clientId: string;
  projects: ProjectRow[];
  bids: BidRow[];
  onXeroChanged: () => void;
}) {
  const navigate = useNavigate();

  const invoiceEmail =
    client.accounts_contact_email || client.billing_email || client.primary_contact_email || null;

  const addressLines = [
    client.address_line1,
    client.address_line2,
    [client.suburb, client.state, client.postcode].filter(Boolean).join(' ') || null,
    client.country && client.country !== 'AU' ? client.country : null,
  ].filter(Boolean) as string[];

  // Every bid on file reads $0 today, so the column is dead weight until one
  // of them carries a figure.
  const anyBidValue = bids.some(b => Number(b.estimated_value ?? 0) > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-3 items-start">

      {/* ---------------- main column: the work ---------------- */}
      <div className="space-y-3 min-w-0">

        <Card>
          <div className="flex items-center justify-between px-4 pt-4 pb-3">
            <div className="text-[13px] font-semibold text-text">
              Projects {projects.length > 0 && <span className="text-text-dim font-normal">· {projects.length}</span>}
            </div>
          </div>
          {projects.length === 0 ? (
            <div className="px-4 pb-5 text-[12px] text-text-muted italic">No projects yet for this client.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider2 text-text-muted border-y border-line">
                    <th className="py-2.5 pl-4 pr-3 font-medium">Project</th>
                    <th className="py-2.5 px-3 font-medium">Status</th>
                    <th className="py-2.5 px-3 font-medium text-right">Contract</th>
                    <th className="py-2.5 px-3 font-medium text-right">Invoiced</th>
                    <th className="py-2.5 pr-4 font-medium text-right">Owing</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => {
                    const owing = (p.invoiced_to_date ?? 0) - (p.paid_to_date ?? 0);
                    return (
                      <tr
                        key={p.project_id}
                        className="border-b border-line/50 last:border-0 hover:bg-ink-hover transition cursor-pointer"
                        onClick={() => navigate(`/projects/${p.project_id}`)}
                      >
                        <td className="py-3 pl-4 pr-3">
                          <div className="text-text">{p.project_name}</div>
                          <div className="text-[11px] text-text-muted font-mono mt-0.5">
                            {p.project_code}
                            <span className="text-text-dim font-sans"> · {fmtDate(p.created_at)}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3"><Pill tone={statusTone(p.status)} label={p.status} small /></td>
                        <td className="py-3 px-3 text-right tabular-nums text-text-muted">{fmtMoney(p.contract_value ?? 0)}</td>
                        <td className="py-3 px-3 text-right tabular-nums text-text">{fmtMoney(p.invoiced_to_date ?? 0)}</td>
                        <td className="py-3 pr-4 text-right tabular-nums">
                          {owing > 0.005
                            ? <span className="text-warn font-medium">{fmtMoney(owing)}</span>
                            : <span className="text-text-dim">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <ClientContactsSection clientId={clientId} />

        <Card>
          <div className="px-4 pt-4 pb-3 text-[13px] font-semibold text-text">
            Bids {bids.length > 0 && <span className="text-text-dim font-normal">· {bids.length}</span>}
          </div>
          {bids.length === 0 ? (
            <div className="px-4 pb-5 text-[12px] text-text-muted italic">No bids for this client.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider2 text-text-muted border-y border-line">
                    <th className="py-2.5 pl-4 pr-3 font-medium">Bid</th>
                    <th className="py-2.5 px-3 font-medium">Status</th>
                    {anyBidValue && <th className="py-2.5 px-3 font-medium text-right">Est. value</th>}
                    <th className="py-2.5 pr-4 font-medium text-right">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((b) => (
                    <tr
                      key={b.bid_id}
                      className="border-b border-line/50 last:border-0 hover:bg-ink-hover transition cursor-pointer"
                      onClick={() => navigate(`/bids/${b.bid_id}`)}
                    >
                      <td className="py-3 pl-4 pr-3">
                        <div className="text-text">{b.bid_title}</div>
                        <div className="text-[11px] text-text-muted font-mono mt-0.5">{b.bid_number}</div>
                      </td>
                      <td className="py-3 px-3"><Pill tone={bidStatusTone(b.status)} label={b.status} small /></td>
                      {anyBidValue && (
                        <td className="py-3 px-3 text-right tabular-nums text-text-muted">
                          {Number(b.estimated_value ?? 0) > 0 ? fmtMoney(b.estimated_value ?? 0) : '—'}
                        </td>
                      )}
                      <td className="py-3 pr-4 text-right text-[12px] text-text-muted">
                        {b.due_date ? fmtDate(b.due_date) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* ---------------- right rail: can we bill them? ---------------- */}
      <div className="space-y-3">

        <Card>
          <div className="p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider2 text-text-muted mb-3">
              <DollarSign size={12}/> Billing
            </div>

            <div className="text-[10px] uppercase tracking-wider2 text-text-dim">Invoices go to</div>
            {invoiceEmail ? (
              <a href={`mailto:${invoiceEmail}`} className="text-[13px] text-accent hover:underline break-all">
                {invoiceEmail}
              </a>
            ) : (
              <div className="text-[12px] text-warn flex items-start gap-1.5 mt-0.5">
                <AlertTriangle size={13} className="mt-px flex-shrink-0"/>
                No accounts email — invoices have nowhere to go.
              </div>
            )}
            {client.accounts_contact_name && (
              <div className="text-[11px] text-text-muted mt-0.5">{client.accounts_contact_name}</div>
            )}
            {client.accounts_contact_phone && (
              <div className="text-[11px] text-text-muted">{client.accounts_contact_phone}</div>
            )}

            <div className="border-t border-line my-3"/>

            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[12px] text-text-muted">Payment terms</span>
              <span className="text-[13px] text-text">
                {client.payment_terms_days ? `${client.payment_terms_days} days` : 'not set'}
              </span>
            </div>

            <div className="border-t border-line my-3"/>

            <div className="text-[10px] uppercase tracking-wider2 text-text-dim mb-1">Billing address</div>
            {addressLines.length > 0 ? (
              <div className="text-[12px] text-text leading-relaxed">
                {addressLines.map((l, i) => <div key={i}>{l}</div>)}
              </div>
            ) : (
              <div className="text-[11px] text-warn flex items-start gap-1.5">
                <AlertTriangle size={13} className="mt-px flex-shrink-0"/>
                Not on file — Xero statements print this, and without it they read “No address”.
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider2 text-text-muted mb-3">
              <ExternalLink size={12}/> Xero
            </div>
            <XeroContactLink
              clientId={client.client_id}
              clientName={client.legal_name}
              contactId={client.xero_contact_id}
              contactName={client.xero_contact_name}
              onChanged={onXeroChanged}
            />
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider2 text-text-muted mb-3">
              <Building2 size={12}/> Company
            </div>
            <dl className="space-y-2 text-[12px]">
              <SideRow label="Legal name" value={client.legal_name}/>
              {client.trading_name && client.trading_name !== client.legal_name && (
                <SideRow label="Trading as" value={client.trading_name}/>
              )}
              <SideRow label="Code" value={client.client_code} mono/>
              <SideRow
                label="ABN"
                mono
                value={client.abn}
                missing="Not recorded — a tax invoice needs it"
              />
              {client.website && (
                <SideRow label="Website" value={
                  <a href={client.website} target="_blank" rel="noopener noreferrer"
                     className="text-accent hover:underline break-all">{client.website}</a>
                }/>
              )}
              {client.uses_rcti && (
                <div className="pt-1">
                  <Pill tone="notice" label="RCTI client" small/>
                  <div className="text-[11px] text-text-muted mt-1">
                    They issue the tax invoice; our invoice mirrors theirs.
                  </div>
                </div>
              )}
              {!client.is_active && (
                <div className="pt-1"><Pill tone="lost" label="Inactive" small/></div>
              )}
            </dl>
          </div>
        </Card>

        {/* 25 Sep 2026 — James: "Add Contracts Library where we can load and
            save the MFA etc". The client's own agreements, kept on the client. */}
        <ClientContractsCard clientId={clientId} />

        {client.notes && (
          <Card>
            <div className="p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider2 text-text-muted mb-2">
                <FileText size={12}/> Internal notes
              </div>
              <div className="text-[12px] text-text whitespace-pre-wrap leading-relaxed">{client.notes}</div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// A label/value pair for the right rail. A value that is simply absent renders
// nothing at all; one that is absent AND matters says why.
function SideRow({ label, value, mono, missing }: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  missing?: string;
}) {
  const empty = value === null || value === undefined || value === '';
  if (empty && !missing) return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-text-muted flex-shrink-0">{label}</dt>
      <dd className={`text-right min-w-0 ${empty ? 'text-warn text-[11px]' : 'text-text'} ${mono && !empty ? 'font-mono text-[11px]' : ''}`}>
        {empty ? missing : value}
      </dd>
    </div>
  );
}
